import { useRef, useState } from "react";

import {
  Breadcrumb,
  Button,
  Col,
  Divider,
  Form,
  Layout,
  message,
  Row,
  Space,
  Table,
} from "antd";

import { PrinterOutlined, TableOutlined } from "@ant-design/icons";
import axios from "axios";
import { sumBy } from "lodash";
import moment from "moment";
import { useCallback } from "react";
import { useSelector } from "react-redux";
import ReactToPrint from "react-to-print";
import DatePickerFieldGroup from "../../commons/DatePickerFieldGroup";
import SelectFieldGroup from "../../commons/SelectFieldGroup";
import { onChange } from "../../utils/form_utilities";
import numberFormat from "../../utils/numberFormat";
import ReportHeading from "../../utils/ReportHeading";
import { addKeysToArray, onCustomerSearch } from "../../utils/utilities";
import { smallFormItemLayout } from "./../../utils/Layouts";

const { Content } = Layout;

const url = "/api/billing-statement/";
const title = "Customer Aging Details";

const initialValues = {
  date: moment(),
  customer: null,
};
export default function CustomerAgingDetails() {
  const [errors, setErrors] = useState({});
  const [records, setRecords] = useState([]);
  const auth = useSelector((state) => state.auth);
  const [state, setState] = useState(initialValues);
  const [options, setOptions] = useState({
    customers: [],
  });
  const [loading_state, setLoadingState] = useState({
    search: false,
    download: false,
  });

  const report = useRef(null);

  const records_column = [
    {
      title: "Type",
      dataIndex: "type",
      width: 200,
    },
    {
      title: "Date",
      dataIndex: "date",
      width: 100,
      render: (date, record) => (
        <span>
          {record.footer !== 1 && date && moment(date).format("M/D/YYYY")}
        </span>
      ),
    },
    {
      title: "Reference",
      dataIndex: "ref_no",
      align: "center",
      width: 100,
      render: (reference, record) => (
        <span
          onClick={(e) => {
            window.open(`/charge-sales?_id=${record._id}`);
          }}
        >
          {reference}
        </span>
      ),
    },
    ,
    /* {
      title: "DR Date",
      dataIndex: "dr_date",
      width: 100,
      render: (date, record) => (
        <span>
          {record.footer !== 1 && date && moment(date).format("M/D/YY")}
        </span>
      ),
    } */ /* {
      title: "SI No.",
      dataIndex: "si_no",
      align: "center",
      width: 100,
    }, */
    {
      title: "Account",
      dataIndex: ["account", "name"],
    },
    {
      title: "Project",
      dataIndex: ["project", "name"],
    },
    {
      title: "Project Type",
      dataIndex: ["project", "project_type"],
    },
    {
      title: "Aging",
      dataIndex: "aging",
      align: "right",
      width: 100,
      render: (aging, record) => <span>{aging}</span>,
    },
    {
      title: "Open Balance",
      dataIndex: "amount",
      align: "right",
      width: 150,
      render: (value, record) => <span>{value && numberFormat(value)}</span>,
    },
  ];

  const getReport = useCallback(({ ...form_data }) => {
    if (form_data.date) {
      setLoadingState((prevState) => ({
        ...prevState,
        search: !form_data.is_excel,
        download: form_data.is_excel,
      }));

      axios
        .post(`${url}customer-aging-details`, form_data)
        .then((response) => {
          if (response.data) {
            if (form_data?.is_excel) {
              setLoadingState((prevState) => ({
                ...prevState,
                download: false,
              }));
              return window.open(`/public/reports/${response.data.filename}`);
            }

            setRecords(response.data.aging);
            setState((prevState) => ({
              ...prevState,
              total: response.data.total,
            }));
            setLoadingState((prevState) => ({
              ...prevState,
              search: false,
            }));
          }
        })
        .catch((err) => {
          message.error("There was an error processing your request");
        })
        .finally(() => {
          setLoadingState((prevState) => ({
            ...prevState,
            search: false,
            download: false,
          }));
        });
    } else {
      message.error("Please select a date");
    }
  }, []);

  return (
    <Content className="content-padding">
      <div className="columns is-marginless">
        <div className="column">
          <Breadcrumb style={{ margin: "16px 0" }}>
            <Breadcrumb.Item>Reports</Breadcrumb.Item>
            <Breadcrumb.Item className="text-color-yellow">
              {title}
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>
      </div>

      <div
        style={{
          background: "#fff",
          padding: 24,
          minHeight: 280,
          borderRadius: 10,
        }}
      >
        <span className="module-title">{title}</span>
        <Divider />
        <Row>
          <Col span={12}>
            <DatePickerFieldGroup
              label="Date"
              name="date"
              value={state.date}
              onChange={(value) => {
                onChange({
                  key: "date",
                  value: value,
                  setState,
                });
              }}
              error={errors.date}
              formItemLayout={smallFormItemLayout}
            />
          </Col>
          <Col span={12}>
            <SelectFieldGroup
              label="Account"
              value={state.account?.name}
              onFocus={() => {
                onCustomerSearch({ value: "", setOptions });
              }}
              onSearch={(value) => onCustomerSearch({ value, setOptions })}
              onChange={(index) => {
                const account = options.accounts?.[index] || null;
                setState((prevState) => ({
                  ...prevState,
                  account,
                }));
              }}
              formItemLayout={smallFormItemLayout}
              data={options.accounts}
              column="name"
              error={errors.account}
              onAddItem={() => accountFormModal.current.open()}
            />
          </Col>
        </Row>

        <Row>
          <Col span={12} offset={4}>
            <Form.Item className="field is-grouped">
              <Space>
                <Button
                  disabled={loading_state.search}
                  loading={loading_state.search}
                  onClick={() => {
                    getReport({ ...state });
                  }}
                >
                  Search
                </Button>
                <Button
                  icon={<TableOutlined />}
                  disabled={loading_state.download}
                  loading={loading_state.download}
                  onClick={() => {
                    getReport({ ...state, is_excel: true });
                  }}
                >
                  Download to Excel
                </Button>
                <ReactToPrint
                  trigger={() => (
                    <Button type="primary" icon={<PrinterOutlined />}>
                      Print
                    </Button>
                  )}
                  bodyClass="print"
                  content={() => report.current}
                />
              </Space>
            </Form.Item>
          </Col>
        </Row>
        <div ref={report}>
          <div className="report-heading">
            <ReportHeading />
            <span className="has-text-weight-bold">{title}</span>
            <br />
            {moment(state.date).format("ll")} <br />
            Printed By : {auth.user.name} <br />
            Date/Time Printed : {moment().format("LLL")}
          </div>
          {records.map((aging, index) => {
            return (
              <div key={index} className="m-t-3">
                <Table
                  title={() => (
                    <div>
                      {aging.dates.length === 2 ? (
                        <span>{`${aging.dates[0]} - ${aging.dates[1]}`}</span>
                      ) : (
                        <span>
                          {aging.dates[0] === 0
                            ? "Current"
                            : `> ${aging.dates[0]}`}
                        </span>
                      )}
                    </div>
                  )}
                  size="small"
                  dataSource={addKeysToArray([
                    ...aging.aging_transactions,
                    {
                      footer: 1,
                      amount: sumBy(aging.aging_transactions, (o) => o.amount),
                    },
                  ])}
                  columns={records_column}
                  pagination={false}
                  rowClassName={(record, index) => {
                    if (record.footer === 1) {
                      return "footer-summary has-text-weight-bold";
                    }
                  }}
                />
              </div>
            );
          })}
          <div className="flex aging-footer">
            <div className="flex-1">Total</div>
            <div className="flex-1">{numberFormat(state.total)}</div>
          </div>
        </div>
      </div>
    </Content>
  );
}
